import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Home, Inbox, ClipboardCheck, Clock, ArrowUpRight, Send, CheckCircle2, MailCheck } from "lucide-react";
import api from "@/lib/api";

function ProgressRing({ value }) {
  const r = 34, c = 2 * Math.PI * r;
  const off = c - (Math.min(value, 100) / 100) * c;
  return (
    <svg width="84" height="84" viewBox="0 0 84 84" className="shrink-0">
      <circle cx="42" cy="42" r={r} fill="none" stroke="#E2DCD0" strokeWidth="7" />
      <circle cx="42" cy="42" r={r} fill="none" stroke="#16A34A" strokeWidth="7" strokeLinecap="round"
        strokeDasharray={c} strokeDashoffset={off} transform="rotate(-90 42 42)" style={{ transition: "stroke-dashoffset .6s ease" }} />
      <text x="42" y="47" textAnchor="middle" fontSize="18" fontFamily="serif" fill="#0F292F">{value}%</text>
    </svg>
  );
}

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [ciStats, setCiStats] = useState(null);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/bookings").then((r) => setBookings(r.data.slice(0, 5))).catch(() => {});
    api.get("/admin/analytics/checkin-stats").then((r) => setCiStats(r.data)).catch(() => {});
  }, []);

  const cards = [
    { label: "Appartamenti", value: stats?.apartments, icon: Home, to: "/admin/apartments" },
    { label: "Richieste totali", value: stats?.bookings, icon: Inbox, to: "/admin/bookings" },
    { label: "Nuove richieste", value: stats?.new_bookings, icon: Clock, to: "/admin/bookings" },
    { label: "Check-in completati", value: stats?.checkins_completed, icon: ClipboardCheck, to: "/admin/checkins" },
  ];

  return (
    <div data-testid="admin-dashboard">
      <h1 className="font-serif font-light text-4xl text-petrol mb-2">Dashboard</h1>
      <p className="text-muted-foreground mb-10">Panoramica della tua attività</p>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
        {cards.map((c) => (
          <Link key={c.label} to={c.to} className="bg-card border border-line rounded-sm p-6 hover:border-petrol/30 transition-all group" data-testid={`stat-${c.label}`}>
            <div className="flex items-center justify-between mb-4">
              <c.icon size={22} strokeWidth={1.5} className="text-gold" />
              <ArrowUpRight size={16} className="text-petrol/30 group-hover:text-gold transition-colors" />
            </div>
            <div className="font-serif text-4xl text-petrol">{c.value ?? "—"}</div>
            <div className="text-sm text-muted-foreground mt-1">{c.label}</div>
          </Link>
        ))}
      </div>

      {/* Check-in completion rate + pre-arrival effectiveness */}
      {ciStats && (
        <div className="mt-10 grid lg:grid-cols-3 gap-5" data-testid="checkin-stats-section">
          <div className="bg-card border border-line rounded-sm p-6 flex items-center gap-5">
            <ProgressRing value={ciStats.completion_rate} />
            <div>
              <div className="text-sm text-muted-foreground">Tasso di completamento check-in</div>
              <div className="font-serif text-3xl text-petrol mt-1" data-testid="completion-rate">{ciStats.completion_rate}%</div>
              <div className="text-xs text-muted-foreground mt-1">{ciStats.completed} completati su {ciStats.total} link</div>
            </div>
          </div>

          <div className="bg-petrol text-white rounded-sm p-6" data-testid="prearrival-effectiveness">
            <div className="flex items-center gap-2 mb-3"><MailCheck size={18} className="text-gold" /><span className="overline text-[10px] text-white/60">Efficacia messaggio pre-arrivo</span></div>
            <div className="font-serif text-4xl text-gold" data-testid="prearrival-conversion">{ciStats.prearrival_conversion}%</div>
            <div className="text-xs text-white/70 mt-1">{ciStats.prearrival_completed} completati su {ciStats.prearrival_sent} che hanno ricevuto il messaggio</div>
            <div className="mt-4 pt-4 border-t border-white/10 text-sm flex items-center justify-between">
              <span className="text-white/70">Senza messaggio</span>
              <span className="text-white/90">{ciStats.no_message_conversion}% ({ciStats.no_message_completed}/{ciStats.no_message_total})</span>
            </div>
          </div>

          <div className="bg-card border border-line rounded-sm p-6 grid grid-cols-2 gap-4 content-center">
            <div className="flex flex-col items-center justify-center p-3 rounded-sm bg-cream">
              <Send size={20} className="text-gold mb-2" />
              <div className="font-serif text-3xl text-petrol" data-testid="prearrival-sent">{ciStats.prearrival_sent}</div>
              <div className="text-xs text-muted-foreground text-center mt-1">Messaggi inviati</div>
            </div>
            <div className="flex flex-col items-center justify-center p-3 rounded-sm bg-cream">
              <CheckCircle2 size={20} className="text-green-600 mb-2" />
              <div className="font-serif text-3xl text-petrol" data-testid="checkin-pending">{ciStats.pending}</div>
              <div className="text-xs text-muted-foreground text-center mt-1">Check-in in attesa</div>
            </div>
          </div>
        </div>
      )}

      <div className="mt-12">
        <h2 className="font-serif text-2xl text-petrol mb-5">Ultime richieste</h2>
        <div className="bg-card border border-line rounded-sm overflow-hidden">
          {bookings.length === 0 ? (
            <p className="p-6 text-muted-foreground text-sm">Nessuna richiesta ancora.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-beige text-petrol/70">
                <tr>
                  <th className="text-left px-5 py-3 font-medium">Ospite</th>
                  <th className="text-left px-5 py-3 font-medium">Appartamento</th>
                  <th className="text-left px-5 py-3 font-medium">Date</th>
                  <th className="text-left px-5 py-3 font-medium">Stato</th>
                </tr>
              </thead>
              <tbody>
                {bookings.map((b) => (
                  <tr key={b.id} className="border-t border-line">
                    <td className="px-5 py-3 text-petrol">{b.full_name}</td>
                    <td className="px-5 py-3 text-muted-foreground">{b.apartment_name}</td>
                    <td className="px-5 py-3 text-muted-foreground">{b.arrival_date} → {b.departure_date}</td>
                    <td className="px-5 py-3"><span className="text-xs px-2 py-1 bg-beige rounded-sm text-petrol">{b.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
